import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import useFormatPrice from "../../../../../../../hooks/useFormatPrice.js";
import useGetUrlParams from "../../../../../../../hooks/useGetUrlParams.js";
import { formatFareName } from "@flights/branded-fares";
import { pricingUtils } from "@flights/web-api-utils-universal";
const useUpgradeFareInfoController = (flight) => {
    const i18n = useI18n();
    const { formatPrice } = useFormatPrice();
    const baseOfferToken = useGetUrlParams().getUrlParam("baseOfferToken");
    const { brandedFareOffers, brandedFareInfo, travellers } = flight;
    const getUniqueUpsellFeatures = (brandedFareOffers) => {
        const baseIncludedFeatures = new Set(brandedFareOffers[0].brandedFareInfo.features.map((f) => f.featureName));
        return [
            ...brandedFareOffers[1].brandedFareInfo.features.filter((feature) => !baseIncludedFeatures.has(feature.featureName))
        ];
    };
    const parentOffer = !baseOfferToken && !!brandedFareOffers;
    const additionalUpsellInclusions = brandedFareOffers && getUniqueUpsellFeatures(brandedFareOffers);
    const canShowUpsellInclusions = parentOffer && !!additionalUpsellInclusions?.length;
    const upgradeFareName = brandedFareOffers && formatFareName(brandedFareOffers[1].brandedFareInfo.fareName);
    const orderedAdditionalUpsellInclusions = additionalUpsellInclusions &&
        FEATURE_ORDER.map((group) => {
            const matchingInclusions = additionalUpsellInclusions.filter((inclusion) => group.featureName.includes(inclusion.featureName));
            return matchingInclusions.length > 0
                ? {
                    group: group.featureGroup,
                    copyTag: group.copyTag,
                    inclusions: matchingInclusions
                }
                : null;
        }).filter(Boolean);
    const incrementalPrice = brandedFareOffers &&
        pricingUtils.subtractPrice(brandedFareOffers[1].priceBreakdown.total, brandedFareOffers[0].priceBreakdown.total);
    const upgradePriceSingleTraveller = incrementalPrice &&
        i18n.trans(t("flights_fd_branded_fare_upsell_incre_price_solo", {
            variables: {
                price_diff: formatPrice(incrementalPrice, { decimalPlaces: 0 })
            }
        }));
    const upgradePriceMultipleTravellers = incrementalPrice &&
        i18n.trans(t("flights_fd_branded_fare_upsell_incre_price_group", {
            variables: {
                price_diff: formatPrice(incrementalPrice, { decimalPlaces: 0 })
            }
        }));
    const price = !!travellers && travellers.length > 1 ? upgradePriceMultipleTravellers : upgradePriceSingleTraveller;
    const firstFeature = orderedAdditionalUpsellInclusions?.[0];
    const secondFeature = orderedAdditionalUpsellInclusions?.[1];
    const getUpsellFeatureContent = () => {
        if ((additionalUpsellInclusions?.length || 0) === 1 && firstFeature) {
            return i18n.trans(t("flights_fd_branded_fare_upsell_extra_feature_1", {
                variables: {
                    feature_1: i18n.trans(firstFeature.copyTag)
                }
            }));
        }
        else if ((additionalUpsellInclusions?.length || 0) === 2 && firstFeature && secondFeature) {
            return i18n.trans(t("flights_fd_branded_fare_upsell_extra_features_2", {
                variables: {
                    feature_1: i18n.trans(firstFeature.copyTag),
                    feature_2: i18n.trans(secondFeature.copyTag)
                }
            }));
        }
        else if ((orderedAdditionalUpsellInclusions?.length || 0) > 2 && firstFeature && secondFeature) {
            return i18n.trans(t("flights_fd_branded_fare_upsell_extra_features_multiple", {
                variables: {
                    feature_1: i18n.trans(firstFeature.copyTag),
                    feature_2: i18n.trans(secondFeature.copyTag)
                }
            }));
        }
        return "";
    };
    const upsellFeatureContent = getUpsellFeatureContent();
    const fareInfoContent = canShowUpsellInclusions && brandedFareInfo && upgradeFareName && additionalUpsellInclusions && upsellFeatureContent
        ? {
            content: upsellFeatureContent,
            price,
            supportText: i18n.trans(t("flights_ancillary_available_next"))
        }
        : undefined;
    return {
        header: i18n.trans(t("flights_fd_branded_fare_upsell_header", {
            variables: {
                fare_name: upgradeFareName || ""
            }
        })),
        headerInline: i18n.trans(t("flights_fd_fare_card_upgrade_available", { variables: { fare_name: upgradeFareName || "" } })),
        subheader: i18n.trans(t("flights_fd_branded_fare_upsell_subheader")),
        fareInfoContent
    };
};
const FEATURE_ORDER = [
    {
        featureGroup: "BAGGAGE",
        featureName: ["CHECK_BAGGAGE"],
        copyTag: t("flights_bf_feature_checked_bagggage")
    },
    {
        featureGroup: "CHANGE",
        featureName: [
            "CHANGEABLE_TICKET",
            "CHANGE_FEE",
            "CHANGE_ANYTIME",
            "CHANGE_AFTER_DEPARTURE",
            "CHANGE_BEFORE_DEPARTURE",
            "SAME_DAY_AS_ORIGINAL_FLT",
            "SAME_DAY_CHANGE"
        ],
        copyTag: t("flights_bf_feature_flight_change")
    },
    {
        featureGroup: "REFUND",
        featureName: ["REFUNDABLE_TICKET", "REFUND_AFTER_DEPARTURE", "REFUND_BEFORE_DEPARTURE"],
        copyTag: t("flights_bf_feature_refund")
    },
    {
        featureGroup: "SEAT",
        featureName: ["BASIC_SEAT", "PREMIUM_SEAT"],
        copyTag: t("flights_bf_feature_seats")
    },
    {
        featureGroup: "PRIORITY",
        featureName: ["FAST_TRACK", "PRIORITY_BOARDING", "PRIORITY_BAGGAGE", "PRIORITY_SECURITY", "LOUNGE_ACCESS"],
        copyTag: t("flights_bf_feature_priority_service")
    }
];
export default useUpgradeFareInfoController;
